import React from 'react';
import { usePersonNavigation } from '../contexts/PersonNavigationContext';
import './PersonBreadcrumb.css';

/**
 * PersonBreadcrumb displays a trail of visited persons as clickable breadcrumb links.
 * Reads from PersonNavigationContext and shows up to 5 most recent entries.
 * Current person (last in trail) is visually distinguished.
 *
 * @param {Object} props
 * @param {function} props.onNavigate - Callback when a breadcrumb is clicked, receives person {id, name}
 * @param {number} [props.maxVisible=5] - Maximum number of breadcrumb entries to display
 */
const PersonBreadcrumb = ({ onNavigate, maxVisible = 5 }) => {
  const { visitedPersons } = usePersonNavigation();

  if (!visitedPersons || visitedPersons.length === 0) {
    return null;
  }

  const isTruncated = visitedPersons.length > maxVisible;
  const visiblePersons = isTruncated
    ? visitedPersons.slice(visitedPersons.length - maxVisible)
    : visitedPersons;

  const handleClick = (person, index) => {
    if (onNavigate) {
      onNavigate(person, index);
    }
  };

  return (
    <nav className="person-breadcrumb" aria-label="Person navigation trail">
      <div className="person-breadcrumb-container">
        {isTruncated && (
          <span className="person-breadcrumb-truncation" aria-label="Earlier entries hidden">
            &hellip;
          </span>
        )}
        {visiblePersons.map((person, idx) => {
          const isLast = idx === visiblePersons.length - 1;
          const originalIndex = isTruncated
            ? visitedPersons.length - maxVisible + idx
            : idx;

          return (
            <React.Fragment key={`${person.id}-${originalIndex}`}>
              {(idx > 0 || isTruncated) && (
                <span className="person-breadcrumb-separator" aria-hidden="true">
                  <i className="fas fa-chevron-right"></i>
                </span>
              )}
              {isLast ? (
                <span
                  className="person-breadcrumb-item person-breadcrumb-current"
                  aria-current="page"
                >
                  {person.name}
                </span>
              ) : (
                <button
                  className="person-breadcrumb-item person-breadcrumb-link"
                  onClick={() => handleClick(person, originalIndex)}
                  type="button"
                >
                  {person.name}
                </button>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </nav>
  );
};

export default PersonBreadcrumb;
